import { z } from 'zod';

// Structured supplier transport rate-sheet metadata (XT-145).
// Consultants read XLS/PDF tables by origin/destination, vehicle, pax and
// validity; this schema is that lookup table in code form.

export const transportRateUnitSchema = z.enum([
  'Vehicle',
  'Pax',
  'Trip',
  'Hour',
  'Day',
]);
export type TransportRateUnit = z.infer<typeof transportRateUnitSchema>;

export const transportRateServiceTypeSchema = z.enum([
  'Transfer',
  'Half',
  'Full',
  'Other',
]);
export type TransportRateServiceType = z.infer<
  typeof transportRateServiceTypeSchema
>;

const nonEmptyTrimmed = z.string().trim().min(1);

const optionalTrimmed = z
  .string()
  .trim()
  .min(1)
  .optional()
  .nullable()
  .transform((v) => v ?? null);

const finiteMoney = z.number().finite().nonnegative();

export const transportRateRowSchema = z
  .object({
    origin: nonEmptyTrimmed,
    destination: nonEmptyTrimmed,
    vehicle: nonEmptyTrimmed,
    serviceType: transportRateServiceTypeSchema.default('Transfer'),
    paxMin: z.number().int().nonnegative().default(1),
    paxMax: z.number().int().positive().nullable().default(null),
    unit: transportRateUnitSchema.default('Vehicle'),
    cost: finiteMoney,
    netCost: finiteMoney.nullable().optional().default(null),
    price: finiteMoney.nullable().optional().default(null),
    currency: z
      .string()
      .trim()
      .min(3)
      .max(3)
      .transform((c) => c.toUpperCase())
      .optional()
      .nullable()
      .default(null),
    notes: optionalTrimmed,
    sourceRef: optionalTrimmed,
  })
  .strict()
  .superRefine((row, ctx) => {
    if (row.paxMax != null && row.paxMax < row.paxMin) {
      ctx.addIssue({
        code: 'custom',
        message: 'paxMax must be >= paxMin',
        path: ['paxMax'],
      });
    }
  });

export type TransportRateRow = z.infer<typeof transportRateRowSchema>;

export const transportRateSheetSchema = z
  .object({
    supplierName: nonEmptyTrimmed,
    supplierId: z.string().uuid().nullable().optional().default(null),
    currency: z
      .string()
      .trim()
      .min(3)
      .max(3)
      .transform((c) => c.toUpperCase())
      .default('EUR'),
    validFrom: z.string().date().nullable().optional().default(null),
    validTo: z.string().date().nullable().optional().default(null),
    title: optionalTrimmed,
    sourceFormat: z.enum(['xlsx', 'pdf', 'json', 'text']).default('json'),
    sourceFileName: optionalTrimmed,
    // Bounded so the estimate/apply endpoints can't be fed unbounded input.
    rows: z.array(transportRateRowSchema).min(1).max(5_000),
    warnings: z.array(z.string()).default([]),
  })
  .strict()
  .superRefine((sheet, ctx) => {
    if (
      sheet.validFrom != null &&
      sheet.validTo != null &&
      sheet.validTo < sheet.validFrom
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'validTo must be on or after validFrom',
        path: ['validTo'],
      });
    }
  });

export type TransportRateSheet = z.infer<typeof transportRateSheetSchema>;

export const parseTransportRateSheet = (
  input: unknown
):
  | { ok: true; sheet: TransportRateSheet }
  | { ok: false; error: z.ZodError } => {
  const parsed = transportRateSheetSchema.safeParse(input);
  if (!parsed.success) return { ok: false, error: parsed.error };
  return { ok: true, sheet: parsed.data };
};

export const normalizePlaceKey = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const normalizeVehicleKey = (value: string): string =>
  normalizePlaceKey(value);

const placeTokens = (value: string): Set<string> =>
  new Set(normalizePlaceKey(value).split(' ').filter(Boolean));

const tokenOverlap = (a: string, b: string): number => {
  const ta = placeTokens(a);
  const tb = placeTokens(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let hit = 0;
  for (const t of ta) if (tb.has(t)) hit += 1;
  return hit / Math.max(ta.size, tb.size);
};

export type TransportRateEstimateQuery = {
  origin: string;
  destination: string;
  vehicle?: string | null;
  pax?: number | null;
  serviceType?: TransportRateServiceType | null;
  onDate?: string | null;
};

export type TransportRateEstimateMatch = {
  row: TransportRateRow;
  score: number;
  reasons: string[];
};

export type TransportRateEstimateResult = {
  currency: string;
  match: TransportRateEstimateMatch | null;
  candidates: TransportRateEstimateMatch[];
  sheetValid: boolean;
  explanation: string;
};

const isDateInValidity = (
  sheet: TransportRateSheet,
  onDate: string | null | undefined
): boolean => {
  if (onDate == null || onDate === '') return true;
  if (sheet.validFrom != null && onDate < sheet.validFrom) return false;
  if (sheet.validTo != null && onDate > sheet.validTo) return false;
  return true;
};

const paxFits = (row: TransportRateRow, pax: number | null | undefined) => {
  if (pax == null) return true;
  if (pax < row.paxMin) return false;
  if (row.paxMax != null && pax > row.paxMax) return false;
  return true;
};

const scoreRow = (
  row: TransportRateRow,
  query: TransportRateEstimateQuery
): TransportRateEstimateMatch | null => {
  const reasons: string[] = [];
  let score = 0;

  const originExact =
    normalizePlaceKey(row.origin) === normalizePlaceKey(query.origin);
  const destExact =
    normalizePlaceKey(row.destination) === normalizePlaceKey(query.destination);

  if (originExact) {
    score += 40;
    reasons.push('origin exact');
  } else {
    const o = tokenOverlap(row.origin, query.origin);
    if (o <= 0) return null;
    score += Math.round(o * 30);
    reasons.push(`origin overlap ${o.toFixed(2)}`);
  }

  if (destExact) {
    score += 40;
    reasons.push('destination exact');
  } else {
    const d = tokenOverlap(row.destination, query.destination);
    if (d <= 0) return null;
    score += Math.round(d * 30);
    reasons.push(`destination overlap ${d.toFixed(2)}`);
  }

  if (query.vehicle) {
    const vExact =
      normalizeVehicleKey(row.vehicle) === normalizeVehicleKey(query.vehicle);
    if (vExact) {
      score += 15;
      reasons.push('vehicle exact');
    } else {
      const v = tokenOverlap(row.vehicle, query.vehicle);
      if (v <= 0) return null;
      score += Math.round(v * 10);
      reasons.push(`vehicle overlap ${v.toFixed(2)}`);
    }
  }

  if (query.serviceType && query.serviceType !== 'Other') {
    if (row.serviceType === query.serviceType) {
      score += 5;
      reasons.push('service type');
    } else if (row.serviceType !== 'Other') {
      return null;
    }
  }

  if (query.pax != null) {
    if (!paxFits(row, query.pax)) return null;
    reasons.push(`pax ${query.pax} in band`);
    score += 5;
  }

  return { row, score, reasons };
};

export const estimateTransferCost = (
  sheet: TransportRateSheet,
  query: TransportRateEstimateQuery
): TransportRateEstimateResult => {
  const sheetValid = isDateInValidity(sheet, query.onDate);
  if (!sheetValid) {
    return {
      currency: sheet.currency,
      match: null,
      candidates: [],
      sheetValid: false,
      explanation: `Sheet validity ${sheet.validFrom ?? '…'}–${sheet.validTo ?? '…'} excludes ${query.onDate}`,
    };
  }

  const scored: TransportRateEstimateMatch[] = [];
  for (const row of sheet.rows) {
    const hit = scoreRow(row, query);
    if (hit) scored.push(hit);
  }
  scored.sort((a, b) => b.score - a.score || a.row.cost - b.row.cost);

  const match = scored[0] ?? null;
  const currency = match?.row.currency ?? sheet.currency;

  if (!match) {
    return {
      currency,
      match: null,
      candidates: [],
      sheetValid: true,
      explanation: `No rate for ${query.origin} → ${query.destination}${
        query.vehicle ? ` / ${query.vehicle}` : ''
      }${query.pax != null ? ` / ${query.pax} pax` : ''}`,
    };
  }

  return {
    currency,
    match,
    candidates: scored.slice(0, 5),
    sheetValid: true,
    explanation: `Best cell ${match.row.origin} → ${match.row.destination}, ${match.row.vehicle}: ${match.row.cost} ${currency} (${match.reasons.join('; ')})`,
  };
};

export type TransferProductPricePatch = {
  cost: number;
  netCost: number | null;
  price: number | null;
  markup: number | null;
};

// Apply a sheet cell onto a single-row product price jsonb, keeping markup
// when present and recomputing sell as cost * (1 + markup/100) when markup
// is set and price was derived from cost. Callers that need VAT-aware
// recompute should use the products pricing engine instead.
export const applyCostToSinglePrice = (
  existing: unknown,
  cost: number,
  opts: { netCost?: number | null; price?: number | null } = {}
): TransferProductPricePatch & Record<string, unknown> => {
  const base =
    existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {};

  const markupRaw = base.markup;
  const markup =
    typeof markupRaw === 'number' && Number.isFinite(markupRaw)
      ? markupRaw
      : null;

  const netCost =
    opts.netCost != null
      ? opts.netCost
      : typeof base.netCost === 'number'
        ? base.netCost
        : cost;

  let price: number | null =
    opts.price != null
      ? opts.price
      : typeof base.price === 'number'
        ? base.price
        : null;

  if (opts.price == null && markup != null && markup >= 0) {
    price =
      Math.round((cost * (1 + markup / 100) + Number.EPSILON) * 100) / 100;
  }

  return {
    ...base,
    cost,
    netCost,
    price,
    markup,
  };
};

// ---------------------------------------------------------------------------
// Planner lookup — what a day's Transport costs for a vehicle
//
// `estimateTransferCost` above answers "this origin → that destination". The
// Mini Planner has no origin/destination: its Transport line is whatever
// vehicle seats the day's largest group, in the chapter's country. So it asks
// a different question — "what does a Minivan for 7 cost in Portugal?" — and
// this is the answer, over the same rate cells the estimator scores.

/** Hours a half- and a full-day charter are billed at (`unit: 'Hour'`). */
export const TRANSPORT_RATE_HALF_DAY_HOURS = 4;
export const TRANSPORT_RATE_FULL_DAY_HOURS = 8;

/**
 * A stored rate cell (`products.transport_rate_row`) with the sheet fields a
 * lookup needs. Keeping the sheet's currency and validity on the row lets the
 * caller load one flat, already-filtered result set instead of re-checking
 * windows per cell.
 */
export type TransportRateLookupRow = {
  sheetId: string;
  supplierName: string;
  /**
   * The country this cell prices, as a name — the same form
   * `organizations.default_pricing.countryValues` uses. The planner asks by
   * its chapter's country, so cells of another country never apply.
   */
  country: string | null;
  vehicle: string;
  vehicleKey: string;
  serviceType: string;
  paxMin: number;
  paxMax: number | null;
  unit: string;
  cost: number;
  /** The supplier's own sell price for the same cell, when the table had one. */
  supplierPrice: number | null;
  currency: string | null;
  sheetCurrency: string;
  validFrom: string | null;
  validTo: string | null;
  /**
   * The planner regions the cell's sheet prices (XT-828); the planner only
   * reads its chapter region's cells.
   */
  regionIds: readonly string[];
  /** A Transfer cell's route; null on a half- or full-day cell. */
  origin: string | null;
  destination: string | null;
};

export type TransportRateVehicleQuery = {
  /** The vehicle the planner picked, e.g. `plannerVehicle(pax).type`. */
  vehicle: string;
  pax: number;
  span: 'half' | 'full';
  onDate?: string | null;
  /** The chapter's country; when set, only cells of that country apply. */
  country?: string | null;
  /**
   * The seats of a vehicle the agent picked: a cell not named like it must
   * seat at least that many, so a smaller car's price never stands in.
   */
  seats?: number | null;
};

export type TransportRateVehicleMatch = {
  row: TransportRateLookupRow;
  /** What the DMC pays, scaled by the cell's unit (`Pax`, `Hour`, flat). */
  cost: number;
  currency: string;
  reasons: string[];
};

/** What the unit charges for: per head, per hour, or the whole vehicle. */
const unitMultiplier = (
  unit: string,
  pax: number,
  span: 'half' | 'full'
): number => {
  if (unit === 'Pax') return pax;
  if (unit === 'Hour') {
    return span === 'half'
      ? TRANSPORT_RATE_HALF_DAY_HOURS
      : TRANSPORT_RATE_FULL_DAY_HOURS;
  }
  // Vehicle | Trip | Day — the cell already prices the whole charter.
  return 1;
};

const cellValid = (
  row: TransportRateLookupRow,
  onDate: string | null | undefined
): boolean => {
  if (onDate == null || onDate === '') return true;
  if (row.validFrom != null && onDate < row.validFrom) return false;
  if (row.validTo != null && onDate > row.validTo) return false;
  return true;
};

/**
 * A cell that prices a day of the car: its own span, or an hourly rate for
 * the car at disposal (`Other` × `Hour`). A Transfer never does (XT-828).
 */
const isDayCell = (row: TransportRateLookupRow, span: 'Half' | 'Full') =>
  row.serviceType === span ||
  (row.serviceType === 'Other' && row.unit === 'Hour');

/** A cell for one trip on a route: a Transfer, or an `Other` not billed by the hour. */
const isRouteCell = (row: TransportRateLookupRow) =>
  row.serviceType === 'Transfer' ||
  (row.serviceType === 'Other' && row.unit !== 'Hour');

/**
 * Whether a cell's pax band can seat the party. A cell with no upper bound
 * (the table had no pax column) only seats it when it is named like the
 * vehicle asked for — otherwise a Sedan with no band would seat 40 (XT-828).
 */
const seats = (
  row: TransportRateLookupRow,
  pax: number,
  named: boolean
): boolean => {
  if (pax < row.paxMin) return false;
  if (row.paxMax == null) return named;
  return pax <= row.paxMax;
};

/** An unnamed cell stands in for a picked vehicle only if it seats as many. */
const seatsPicked = (
  row: TransportRateLookupRow,
  wanted: number | null | undefined
): boolean => wanted == null || (row.paxMax ?? 0) >= wanted;

const vehicleOverlap = (row: TransportRateLookupRow, vehicle: string) => {
  const key = row.vehicleKey || normalizeVehicleKey(row.vehicle);
  return key === normalizeVehicleKey(vehicle)
    ? 1
    : tokenOverlap(row.vehicle, vehicle);
};

/**
 * Picks among candidate cells: a cell named like the vehicle wins, ranked by
 * `score` then cost. When none is named — the planner's list is English
 * ("Minivan"), a supplier's may be "Carrinha" or "Coach" — the tightest pax
 * band that seats the party is that group's vehicle, cost breaking the tie;
 * only cells with a real band take part.
 */
const pickCell = (
  candidates: ReadonlyArray<{
    row: TransportRateLookupRow;
    score: number;
    named: boolean;
    reasons: string[];
  }>,
  costOf: (row: TransportRateLookupRow) => number
): { row: TransportRateLookupRow; reasons: string[] } | null => {
  const named = candidates
    .filter((hit) => hit.named)
    .sort((a, b) => b.score - a.score || costOf(a.row) - costOf(b.row));
  if (named[0]) return named[0];
  const banded = candidates
    .filter((hit) => hit.row.paxMax != null)
    .sort(
      (a, b) =>
        (a.row.paxMax as number) -
          a.row.paxMin -
          ((b.row.paxMax as number) - b.row.paxMin) ||
        costOf(a.row) - costOf(b.row)
    );
  const nearest = banded[0];
  return nearest
    ? {
        row: nearest.row,
        reasons: [...nearest.reasons, 'no named vehicle; nearest pax band'],
      }
    : null;
};

/**
 * The cell that prices a half or full day of `query.vehicle` for
 * `query.pax`, or null when the supplier tables do not cover it.
 *
 * Only a day cell counts (`isDayCell`): a Transfer is one route, never a day
 * of the car (XT-828). The
 * cell must be valid on the day, priced for the country, and seat the party.
 */
export const lookupTransportVehicleRate = (
  rows: readonly TransportRateLookupRow[],
  query: TransportRateVehicleQuery
): TransportRateVehicleMatch | null => {
  const wantedCountry = query.country?.trim().toLowerCase() ?? null;
  const serviceType = query.span === 'half' ? 'Half' : 'Full';
  const candidates = rows.flatMap((row) => {
    if (!isDayCell(row, serviceType)) return [];
    if (!cellValid(row, query.onDate)) return [];
    if (
      wantedCountry != null &&
      (row.country ?? '').trim().toLowerCase() !== wantedCountry
    ) {
      return [];
    }
    const overlap = vehicleOverlap(row, query.vehicle);
    const named = overlap > 0;
    if (!seats(row, query.pax, named)) return [];
    if (!named && !seatsPicked(row, query.seats)) return [];
    return [
      {
        row,
        named,
        score: overlap === 1 ? 30 : Math.round(overlap * 20),
        reasons: [
          overlap === 1
            ? 'vehicle exact'
            : `vehicle overlap ${overlap.toFixed(2)}`,
          `${query.span} day`,
        ],
      },
    ];
  });
  const costOf = (row: TransportRateLookupRow) =>
    row.cost * unitMultiplier(row.unit, query.pax, query.span);
  const best = pickCell(candidates, costOf);
  if (!best) return null;
  return {
    row: best.row,
    cost: costOf(best.row),
    currency: best.row.currency ?? best.row.sheetCurrency,
    reasons: best.reasons,
  };
};

export type TransportRateRouteQuery = {
  /** Where the car picks up — the chapter's hotel (its town). */
  from: string;
  /** Where it goes — the experience's start (its town). */
  to: string;
  vehicle: string;
  pax: number;
  onDate?: string | null;
  country?: string | null;
  /** As `TransportRateVehicleQuery.seats`. */
  seats?: number | null;
};

/** How well a cell's place matches one asked for: 1 exact, else word overlap. */
const placeMatch = (cell: string | null, wanted: string): number => {
  if (!cell) return 0;
  return normalizePlaceKey(cell) === normalizePlaceKey(wanted)
    ? 1
    : tokenOverlap(cell, wanted);
};

/**
 * The Transfer cell for the route `from` → `to` (either way round), or null.
 * Both ends must match a word of the cell's route; the vehicle and pax band
 * follow the same rules as a day (`seats`, `pickCell`). A one-experience day's
 * Transport is priced from it first (XT-828).
 */
export const lookupTransportRouteRate = (
  rows: readonly TransportRateLookupRow[],
  query: TransportRateRouteQuery
): TransportRateVehicleMatch | null => {
  const wantedCountry = query.country?.trim().toLowerCase() ?? null;
  const candidates = rows.flatMap((row) => {
    if (!isRouteCell(row)) return [];
    if (!cellValid(row, query.onDate)) return [];
    if (
      wantedCountry != null &&
      (row.country ?? '').trim().toLowerCase() !== wantedCountry
    ) {
      return [];
    }
    const forward = Math.min(
      placeMatch(row.origin, query.from),
      placeMatch(row.destination, query.to)
    );
    const backward = Math.min(
      placeMatch(row.origin, query.to),
      placeMatch(row.destination, query.from)
    );
    const route = Math.max(forward, backward);
    if (route <= 0) return [];
    const overlap = vehicleOverlap(row, query.vehicle);
    const named = overlap > 0;
    if (!seats(row, query.pax, named)) return [];
    if (!named && !seatsPicked(row, query.seats)) return [];
    return [
      {
        row,
        named,
        score: Math.round(route * 40) + Math.round(overlap * 20),
        reasons: [
          `route ${route.toFixed(2)}`,
          overlap === 1
            ? 'vehicle exact'
            : `vehicle overlap ${overlap.toFixed(2)}`,
        ],
      },
    ];
  });
  // A transfer is one trip: `Hour` cells bill a half day's hours.
  const costOf = (row: TransportRateLookupRow) =>
    row.cost * unitMultiplier(row.unit, query.pax, 'half');
  const best = pickCell(candidates, costOf);
  if (!best) return null;
  return {
    row: best.row,
    cost: costOf(best.row),
    currency: best.row.currency ?? best.row.sheetCurrency,
    reasons: best.reasons,
  };
};
